import { formatMoney } from '@theme/money-formatting';

/**
 * <atc-total>: het bedrag in de koopknop ("In winkelwagen · € 29,95").
 *
 * Met Kaching volgt het de gekozen pack-kaart: de prijs die Kaching op de geselecteerde
 * kaart zet, is ook wat er in de winkelwagen komt. Zonder Kaching (een product zonder
 * deal, of een wholesale-klant voor wie Kaching niet start) is het de stukprijs maal
 * het aantal in de eigen aantalkiezer.
 *
 * Kaching rendert asynchroon en zet de keuze met klassen, niet met een event waar het
 * thema op kan rekenen, dus een MutationObserver kijkt mee. Lukt geen van beide, dan
 * blijft het bedrag uit Liquid staan: de prijs van één stuk.
 */
class AtcTotal extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const form = this.closest('form');
    form?.addEventListener('input', this.#update, { signal });
    form?.addEventListener('change', this.#update, { signal });

    const kaching = document.querySelector('kaching-bundle');
    if (kaching) {
      this.#observer = new MutationObserver(this.#update);
      this.#observer.observe(kaching, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  #update = () => {
    const value = this.querySelector('[data-atc-total-value]');
    if (!value) return;

    const text = this.#kachingTotal() || this.#nativeTotal();
    if (text && value.textContent !== text) value.textContent = text;
  };

  /** @returns {string} */
  #kachingTotal() {
    const price = document.querySelector('.kaching-bundles__bar--selected .kaching-bundles__bar-price');
    return price?.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  }

  /** @returns {string} */
  #nativeTotal() {
    const { unitPrice, moneyFormat, currency } = this.dataset;
    if (!unitPrice || !moneyFormat || !currency) return '';

    const input = this.closest('form')?.querySelector('input[name="quantity"]');
    const quantity = input instanceof HTMLInputElement ? parseInt(input.value, 10) : 1;
    if (!Number.isFinite(quantity) || quantity < 1) return '';

    return formatMoney(parseInt(unitPrice, 10) * quantity, moneyFormat, currency);
  }
}

if (!customElements.get('atc-total')) {
  customElements.define('atc-total', AtcTotal);
}
